import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';

export default function AdminDashboard() {
  const [stats, setStats] = useState({ bookings: 0, contacts: 0, reviews: 0 });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [bookingsRes, contactsRes, reviewsRes] = await Promise.all([
        fetch(`${backendUrl}/api/admin/bookings`, { headers }),
        fetch(`${backendUrl}/api/admin/contacts?limit=5`, { headers }),
        fetch(`${backendUrl}/api/admin/reviews`, { headers })
      ]);

      const bookings = await bookingsRes.json();
      const contacts = await contactsRes.json();
      const reviews = await reviewsRes.json();

      // Calculate total revenue from all bookings
      const totalRevenue = bookings.reduce((sum, booking) => {
        return sum + (booking.package?.total_price || 0);
      }, 0);

      // Count completed jobs
      const completedJobs = bookings.filter(b => b.status === 'completed').length;

      setStats({
        bookings: bookings.length,
        contacts: contacts.length,
        reviews: reviews.filter(r => !r.approved).length,
        totalRevenue: totalRevenue,
        totalJobs: bookings.length,
        completedJobs: completedJobs
      });
      
      setRecentBookings(bookings.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard:', error);
      if (error.message.includes('401')) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };
  void handleLogout;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <AdminLayout>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Overview</h2>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                  <p className="text-3xl font-bold text-green-600">A${stats.totalRevenue?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-gray-500 mt-1">All bookings</p>
                </div>
                <div className="bg-green-100 rounded-full p-3">
                  <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Jobs</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.totalJobs || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">{stats.completedJobs || 0} completed</p>
                </div>
                <div className="bg-blue-100 rounded-full p-3">
                  <i className="ri-briefcase-line text-2xl text-blue-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pending Bookings</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.bookings}</p>
                </div>
                <div className="bg-blue-100 rounded-full p-3">
                  <i className="ri-calendar-line text-2xl text-blue-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">New Contacts</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.contacts}</p>
                </div>
                <div className="bg-green-100 rounded-full p-3">
                  <i className="ri-mail-line text-2xl text-green-600"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pending Reviews</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.reviews}</p>
                </div>
                <div className="bg-orange-100 rounded-full p-3">
                  <i className="ri-star-line text-2xl text-orange-600"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Bookings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Recent Bookings</h3>
            </div>
            <div className="p-6">
              {recentBookings.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No bookings yet</p>
              ) : (
                <div className="space-y-4">
                  {recentBookings.map((booking) => (
                    <div key={booking.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{booking.customer_name}</p>
                        <p className="text-sm text-gray-600">{booking.customer_email}</p>
                        <p className="text-sm text-gray-500">{booking.booking_date} at {booking.booking_time}</p>
                      </div>
                      <div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {booking.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
    </AdminLayout>
  );
}
